import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import { setUserContext } from '@/lib/monitoring';
import { savedHotelsStorage } from '@/utils/localStorage';

type AuthContextType = {
  loading: boolean;
  user: User | null;
};

const AuthContext = createContext<AuthContextType>({ loading: true, user: null });

export const useAuth = () => useContext(AuthContext);

// Merge localStorage saves to server after login
async function mergeLocalSavesToServer(userId: string) {
  const localIds = savedHotelsStorage.getAll();
  
  if (localIds.length === 0) return;

  console.log(`🔄 Merging ${localIds.length} local saves to server for user ${userId}`);

  try {
    // Fetch existing server saves
    const { data: existing } = await supabase
      .from('saved_properties')
      .select('hotel_id')
      .eq('user_id', userId);

    const existingIds = new Set(existing?.map(s => s.hotel_id) || []);

    // Insert only new ones
    const toInsert = localIds
      .filter(id => !existingIds.has(id))
      .map(hotel_id => ({ 
        user_id: userId, 
        hotel_id,
        property_name: `Hotel ${hotel_id}` // Placeholder name
      }));

    if (toInsert.length > 0) {
      const { error } = await supabase.from('saved_properties').insert(toInsert);
      if (error) {
        console.error('❌ Error merging local saves to server:', error);
        return;
      }
      console.log(`✅ Merged ${toInsert.length} new saves to server`);
    }

    // Clear localStorage after successful merge
    savedHotelsStorage.clear();
  } catch (error) {
    console.error('❌ Failed to merge local saves:', error);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthContextType>({ loading: true, user: null });
  const previousUserRef = useRef<User | null>(null);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      const previousUser = previousUserRef.current;
      
      setState({ loading: false, user });
      
      // Update Sentry user context for error tracking
      if (user) {
        setUserContext({
          id: user.id,
          email: user.email,
        });
        
        // User just logged in - merge localStorage to server
        if (!previousUser && user) {
          mergeLocalSavesToServer(user.id);
        }
      } else {
        setUserContext(null);
      }
      
      previousUserRef.current = user;
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null;
      setState({ loading: false, user });
      
      // Update Sentry user context for error tracking
      if (user) {
        setUserContext({
          id: user.id,
          email: user.email,
        });
      } else {
        setUserContext(null);
      }
      
      previousUserRef.current = user;
    });

    return () => subscription.unsubscribe();
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
